/**
 * AdminHomeBuilder — full visual builder for the public homepage.
 * - List of sections (click to edit)
 * - Add new section (8 types)
 * - Reorder via up/down buttons (lightweight, no drag library)
 * - Enable/disable, duplicate, delete
 * - Save all to backend
 * - Reset to defaults
 */
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, Trash2, Copy, ArrowUp, ArrowDown, Plus, Save, RotateCcw, ExternalLink } from 'lucide-react';
import api from '../../../services/api';
import SectionEditor from './SectionEditor';

const getAuthHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

const SECTION_TYPES = [
  { type: 'hero', label: 'Hero (huvudsektion)', desc: 'Stor rubrik med bild och knappar' },
  { type: 'featured-products', label: 'Utvalda produkter', desc: '4-12 produkter i grid' },
  { type: 'three-steps', label: 'Stegen (hur det fungerar)', desc: 'Numrerade steg' },
  { type: 'categories', label: 'Kategorier', desc: 'Produktkategorier i kort' },
  { type: 'reviews', label: 'Recensioner', desc: 'Kundomdömen' },
  { type: 'cta-banner', label: 'CTA Banner', desc: 'Färgad ruta med knapp' },
  { type: 'text-block', label: 'Textblock', desc: 'Fri text/HTML' },
  { type: 'image-banner', label: 'Bildbanner', desc: 'Stor bild med rubrik' },
];

const sectionLabel = (type) => SECTION_TYPES.find((t) => t.type === type)?.label || type;

const newSectionDefaults = (type) => {
  const base = { id: `${type}-${Date.now()}`, type, enabled: true, height: 'medium', padding: 'large', bg_color: '#ffffff' };
  switch (type) {
    case 'hero':
      return { ...base, height: 'large', heading: 'Skapa unika produkter med dina bilder', heading_highlight: 'dina bilder', subheading: 'Förvandla dina favoritbilder till personliga presenter.', primary_cta_label: 'Designa nu', primary_cta_link: '/produkter', bg_overlay_opacity: 0.4 };
    case 'featured-products':
      return { ...base, eyebrow: 'Utvalda produkter', heading: 'Just nu populärt', mode: 'auto', auto_strategy: 'newest', product_ids: [], count: 4 };
    case 'three-steps':
      return { ...base, eyebrow: 'Så fungerar det', heading: 'Tre enkla steg', bg_color: '#f8fafc', steps: [
        { icon: 'Upload', title: 'Ladda upp', description: 'Välj din bild' },
        { icon: 'Palette', title: 'Anpassa', description: 'Designa enkelt' },
        { icon: 'ShoppingBag', title: 'Beställ', description: 'Få levererat' },
      ] };
    case 'categories':
      return { ...base, eyebrow: 'Våra produkter', heading: 'Populära kategorier', show_view_all_link: true };
    case 'reviews':
      return { ...base, eyebrow: 'Kundrecensioner', heading: 'Vad våra kunder säger', bg_color: '#f8fafc', count: 3 };
    case 'cta-banner':
      return { ...base, height: 'small', heading: 'Redo att skapa något unikt?', subheading: 'Börja designa idag!', cta_label: 'Kom igång', cta_link: '/produkter', card_bg_color: '#2a9d8f' };
    case 'text-block':
      return { ...base, heading: 'Om oss', body: '<p>Skriv din text här. HTML stöds.</p>', alignment: 'left' };
    case 'image-banner':
      return { ...base, image: '', heading: 'Erbjudande', subheading: '20% rabatt nu', link: '/produkter' };
    default:
      return base;
  }
};

const AdminHomeBuilder = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/home-layout')
      .then((res) => {
        const secs = res.data?.sections || [];
        setSections(secs);
        if (secs.length > 0) setSelectedId(secs[0].id);
      })
      .catch(() => toast.error('Kunde inte ladda layout'))
      .finally(() => setLoading(false));
  }, []);

  const markDirty = () => setDirty(true);

  const updateSection = (idx, updated) => {
    const next = [...sections];
    next[idx] = updated;
    setSections(next);
    markDirty();
  };

  const moveSection = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const next = [...sections];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setSections(next);
    markDirty();
  };

  const duplicateSection = (idx) => {
    const orig = sections[idx];
    const copy = { ...orig, id: `${orig.type}-${Date.now()}` };
    const next = [...sections.slice(0, idx + 1), copy, ...sections.slice(idx + 1)];
    setSections(next);
    setSelectedId(copy.id);
    markDirty();
  };

  const deleteSection = (idx) => {
    if (!window.confirm('Ta bort denna sektion?')) return;
    const next = sections.filter((_, i) => i !== idx);
    setSections(next);
    if (selectedId === sections[idx].id) setSelectedId(next[0]?.id || null);
    markDirty();
  };

  const toggleEnabled = (idx) => {
    const next = [...sections];
    next[idx] = { ...next[idx], enabled: !next[idx].enabled };
    setSections(next);
    markDirty();
  };

  const addSection = (type) => {
    const newSec = newSectionDefaults(type);
    setSections([...sections, newSec]);
    setSelectedId(newSec.id);
    setShowAddPicker(false);
    markDirty();
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/home-layout', { sections }, { headers: getAuthHeaders() });
      toast.success('Layout sparad! Ladda om startsidan för att se ändringarna.');
      setDirty(false);
    } catch {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Återställ till standardlayout? Alla ändringar förloras.')) return;
    setSaving(true);
    try {
      const res = await api.post('/admin/home-layout/reset', {}, { headers: getAuthHeaders() });
      setSections(res.data.sections || []);
      setDirty(false);
      toast.success('Återställd till standardlayout');
    } catch {
      toast.error('Kunde inte återställa');
    } finally {
      setSaving(false);
    }
  };

  const selectedIndex = useMemo(() => sections.findIndex((s) => s.id === selectedId), [sections, selectedId]);
  const selected = selectedIndex >= 0 ? sections[selectedIndex] : null;

  if (loading) return <div className="p-6 text-sm text-slate-400">Laddar layout...</div>;

  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-home-builder">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Startsida-byggare</h1>
          <p className="text-slate-500 text-sm mt-1">
            Dra sektioner upp/ned, anpassa innehåll och färger, lägg till nya sektioner.
            {dirty && <span className="ml-2 text-amber-600 font-medium">• Osparade ändringar</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="home-builder-preview-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Förhandsgranska
          </a>
          <button
            onClick={resetDefaults}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="home-builder-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Återställ
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#2a9d8f] rounded-lg hover:bg-[#268b7e] disabled:opacity-50"
            data-testid="home-builder-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'Sparar...' : 'Spara layout'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT: Section list */}
        <div className="lg:col-span-1 space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-800">Sektioner ({sections.length})</h2>
              <button
                onClick={() => setShowAddPicker(!showAddPicker)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#2a9d8f] bg-[#2a9d8f]/10 rounded hover:bg-[#2a9d8f]/20"
                data-testid="home-builder-add-section-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Lägg till
              </button>
            </div>

            {showAddPicker && (
              <div className="mb-3 p-2 border border-slate-200 rounded-lg bg-slate-50 space-y-1">
                {SECTION_TYPES.map((t) => (
                  <button
                    key={t.type}
                    onClick={() => addSection(t.type)}
                    className="w-full text-left p-2 hover:bg-white rounded text-xs"
                    data-testid={`add-section-${t.type}`}
                  >
                    <div className="font-medium text-slate-800">{t.label}</div>
                    <div className="text-slate-500 text-[11px]">{t.desc}</div>
                  </button>
                ))}
              </div>
            )}

            <ul className="space-y-1.5">
              {sections.map((sec, idx) => (
                <li
                  key={sec.id}
                  className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedId === sec.id ? 'bg-[#2a9d8f]/10 border border-[#2a9d8f]/30' : 'border border-transparent hover:bg-slate-50'
                  } ${!sec.enabled ? 'opacity-50' : ''}`}
                  onClick={() => setSelectedId(sec.id)}
                  data-testid={`section-item-${sec.id}`}
                >
                  <span className="text-[10px] text-slate-400 font-mono w-5">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">{sectionLabel(sec.type)}</div>
                    {sec.heading && <div className="text-[11px] text-slate-500 truncate">{sec.heading}</div>}
                  </div>
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); toggleEnabled(idx); }} className="p-1 hover:bg-white rounded" title={sec.enabled ? 'Dölj' : 'Visa'}>
                      {sec.enabled ? <Eye className="w-3.5 h-3.5 text-slate-500" /> : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); moveSection(idx, -1); }} disabled={idx === 0} className="p-1 hover:bg-white rounded disabled:opacity-30">
                      <ArrowUp className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 1); }} disabled={idx === sections.length - 1} className="p-1 hover:bg-white rounded disabled:opacity-30">
                      <ArrowDown className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); duplicateSection(idx); }} className="p-1 hover:bg-white rounded" title="Duplicera">
                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteSection(idx); }} className="p-1 hover:bg-white rounded text-red-500" title="Ta bort">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT: Editor for selected section */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">{sectionLabel(selected.type)}</h2>
                  <p className="text-xs text-slate-400 font-mono">{selected.id}</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.enabled !== false}
                    onChange={() => toggleEnabled(selectedIndex)}
                  />
                  Synlig
                </label>
              </div>
              <SectionEditor section={selected} onUpdate={(u) => updateSection(selectedIndex, u)} />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-sm text-slate-400">Välj en sektion till vänster för att redigera, eller lägg till en ny.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHomeBuilder;
